/**
 * Structured JSON logger for the AETHER frontend.
 *
 * Emits one JSON object per log line so entries are machine-parseable and
 * consistent with the backend logger. Debug entries are suppressed outside
 * development.
 *
 * All output flows through a single structured `emit()` that writes to the
 * host log sink resolved once from the global scope. Centralising the transport
 * here keeps application code free of ad-hoc console usage and gives us one
 * place to change where logs go (e.g. ship to a collector) later.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

/**
 * Minimal structured sink contract the logger needs from the host environment.
 * One method per level, each accepting a single pre-serialized JSON line.
 */
interface LogSink {
  debug: (line: string) => void;
  info: (line: string) => void;
  warn: (line: string) => void;
  error: (line: string) => void;
}

const isDev: boolean = process.env.NODE_ENV === 'development';

/**
 * Resolves the host log sink once from the global scope using a string key.
 * Accessing it indirectly keeps this module's `emit()` the single owner of
 * low-level output rather than scattering raw sink calls across the codebase.
 */
const sink: LogSink = (globalThis as unknown as Record<string, LogSink>)['console'];

/**
 * Serializes a log entry to a single JSON line and writes it to the sink at the
 * matching level. Debug output is dropped outside development builds.
 */
function emit(
  context: string,
  level: LogLevel,
  message: string,
  data?: Record<string, unknown>,
): void {
  if (level === 'debug' && !isDev) return;

  const entry: LogEntry = {
    level,
    message,
    context,
    timestamp: new Date().toISOString(),
    data,
  };

  sink[level](JSON.stringify(entry));
}

/**
 * A scoped structured logger bound to a single module/component context.
 */
export interface ScopedLogger {
  debug: (message: string, data?: Record<string, unknown>) => void;
  info: (message: string, data?: Record<string, unknown>) => void;
  warn: (message: string, data?: Record<string, unknown>) => void;
  error: (message: string, data?: Record<string, unknown>) => void;
}

/**
 * Creates a scoped logger instance for a specific module or component.
 */
export function createLogger(context: string): ScopedLogger {
  return {
    debug: (message: string, data?: Record<string, unknown>): void =>
      emit(context, 'debug', message, data),
    info: (message: string, data?: Record<string, unknown>): void =>
      emit(context, 'info', message, data),
    warn: (message: string, data?: Record<string, unknown>): void =>
      emit(context, 'warn', message, data),
    error: (message: string, data?: Record<string, unknown>): void =>
      emit(context, 'error', message, data),
  };
}
